import Constants from 'expo-constants';
import React, {
  createContext,
  PropsWithChildren,
  useEffect,
  useState,
} from 'react';
import { isWeb } from 'shared/constants/platform';
import { Environment } from 'shared/providers/environment.constants';
import getEnvironmentVar from 'shared/utils/getEnvironmentVar';

const LOCALHOST_URL = 'http://localhost:8081';
export const UAT_AUTH_URL = 'https://api-uat.varomoney.com';
export const UAT_AUTH_V2_URL = 'https://auth-uat.varomoney.com/v2';
const PROD_AUTH_URL = 'https://api-prod.varomoney.com';
const PROD_AUTH_V2_URL = 'https://auth-prod.varomoney.com/v2';
const UAT_URL = 'https://uat-bank.varomoney.com';
const PROD_URL = 'https://bank.varomoney.com';
const UAT_WS_URL = 'uat-bank.varomoney.com';
const PROD_WS_URL = 'bank.varomoney.com';

export interface EnvironmentContextProps {
  currentEnv: Environment;
  setCurrentEnv: (env: Environment) => void;
  getGQLUrl: (env: Environment) => string;
  getLambdaUrl: (env: Environment) => string;
  getMapsUrl: (env: Environment) => string;
  getV2AuthUrl: (env: Environment) => string;
}

export const EnvironmentContext = createContext<EnvironmentContextProps>(
  {} as EnvironmentContextProps,
);

// TODO: JT-116715 - Fix GQL and Maps URLs once we have an actual URL for Cruise envs
export const getGQLUrl = (env: Environment): string => {
  if (__DEV__) {
    switch (env) {
      case Environment.PROD:
        return `${LOCALHOST_URL}/api/${Environment.PROD}`;
      default:
        return `${LOCALHOST_URL}/api/${Environment.UAT}`;
    }
  }

  switch (env) {
    case Environment.PROD:
      return `${PROD_URL}/api/graphql`;
    default:
      return `${UAT_URL}/api/graphql`;
  }
};

export const getMapsUrl = (env: Environment): string => {
  if (__DEV__) {
    return LOCALHOST_URL;
  }

  switch (env) {
    case Environment.PROD:
      return PROD_URL;
    default:
      return UAT_URL;
  }
};

export const getLambdaUrl = (env: Environment): string => {
  switch (env) {
    case Environment.PROD:
      return PROD_AUTH_URL;
    default:
      return UAT_AUTH_URL;
  }
};

export const getV2AuthUrl = (env: Environment): string => {
  switch (env) {
    case Environment.PROD:
      return PROD_AUTH_V2_URL;
    default:
      return UAT_AUTH_V2_URL;
  }
};

export const getWsUrl = (env: Environment): string => {
  let ws;

  switch (env) {
    case Environment.PROD:
      ws = PROD_WS_URL;
      break;
    default:
      ws = UAT_WS_URL;
  }
  return `wss://${__DEV__ ? UAT_WS_URL : ws}/api/subscription`;
};

export const getReferralLambdaUrl = (env: Environment): string => {
  return `https://${env}-referral-lambda.varomoney.com/referral/validate`;
};

export const getAttributionLambdaUrl = (env: Environment): string => {
  return `https://${env}-api-customer-attribution.varomoney.com/webhook`;
};

export const getReceiverV2ALambda = (env: Environment): string => {
  switch (env) {
    case Environment.PROD:
      return `https://${env}-us-west-2-varo-to-anyone.varomoney.com/`;
    default:
      return `https://uat-us-west-2-varo-to-anyone.varomoney.com/`;
  }
};

export const getReferralHeaderUrl = (env: Environment): string => {
  switch (env) {
    case Environment.PROD:
      return PROD_URL;
    default:
      return UAT_URL;
  }
};

export const getSmartyStreetsBaseUrl = (): string => {
  return 'https://us-autocomplete-pro.api.smarty.com/lookup';
};

export const getUserAlreadyRegisteredBiometricsKey = (): string => {
  return 'tHLfCBbTfgb1ApcuN8SV4T7yf$3tAL';
};

export const getRefreshTokenKeyExistingiOSUser = (): string => {
  return 'OGTRAVd0iD^mh7oBQKJpWHQjwb@0iN';
};

export const getExistingUserIDForBiometricsKey = (): string => {
  return 'Sg46&Hf8Iw5PIQ2Hlh4KS!K9$**76I';
};

export const getExistingUserRegistrationForDevice = (): string => {
  return 'EXISTING_USER_REGISTER_FOR_DEVICE';
};

export const getRefreshTokenKeyReactNativeUser = (): string => {
  return 'REFRESH_TOKEN_KEY_REACT_NATIVE_USER';
};

export const getAndroidUserIDForBiometricsPhoneLoginKey = (): string => {
  return 'e44e2676-4a4a-4e96-bb3d-d744bfee265d';
};

export const getAndroidUserIDForBiometricsEmailLoginKey = (): string => {
  return 'ef5895b0-b025-11ea-b3de-0242ac130004';
};

export const getUserIDForBiometricsKey = (): string => {
  return 'USERID_FOR_BIOMETRICS_KEY';
};

export const getUserHasEnabledBiometricsKey = (): string => {
  return 'USER_HAS_ENABLED_BIOMETRICS_KEY';
};

export const getBiometricToggleSettingKey = (): string => {
  return 'BIOMETRIC_SETTINGS_KEY';
};

export const getHasPermanentRefreshTokenKey = (): string => {
  return 'HAS_PERMANENT_REFRESH_TOKEN_KEY';
};

export const getCvvDataKey = (cardId: string): string => {
  return `CVV_DATA_KEY_${cardId}`;
};

const EnvironmentProvider = ({ children }: PropsWithChildren) => {
  const processEnv = getEnvironmentVar('EXPO_ENV') as Environment;
  const [currentEnv, setCurrentEnv] = useState(
    (processEnv?.toLowerCase() as Environment) ?? Environment.UAT,
  );

  useEffect(() => {
    if (isWeb) {
      if (__DEV__ && Constants.expoConfig?.extra?.EXPO_ENV) {
        setCurrentEnv(Constants.expoConfig?.extra?.EXPO_ENV as Environment);

        return;
      }

      if (window.location.hostname === 'prod-cruise-web.varomoney.com') {
        setCurrentEnv(Environment.PROD);
        return;
      }

      switch (`${window.location.protocol}//${window.location.hostname}`) {
        case PROD_URL:
          setCurrentEnv(Environment.PROD);
          break;
        default:
          setCurrentEnv(Environment.UAT);
      }
    }
  }, []);

  return (
    <EnvironmentContext.Provider
      value={{
        currentEnv,
        setCurrentEnv,
        getGQLUrl,
        getLambdaUrl,
        getMapsUrl,
        getV2AuthUrl,
      }}
    >
      {children}
    </EnvironmentContext.Provider>
  );
};

export default EnvironmentProvider;
